<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { ROUTE_NAMES } from '@/routes/route-helpers'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { RADIO_GROUP_TYPES } from '@/utils/components-configurations/app-radio-group'
import { DROPDOWN_MENU_TYPES } from '@/utils/components-configurations/dropdown-menu'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { openLink } from '@/utils/external-link-helper'
import { CHECKINS_GUIDE_LINK, CHECKINS_TUTORIAL_LINK } from '@/utils/links'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

import { CHECKINS_SETTINGS_PAGE_TABS_TEST_ID } from '@/components/check-ins/jest-helpers'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppRadioGroup from '@/components/ui/AppRadioGroup/AppRadioGroup'
import DropdownMenu from '@/components/ui/DropdownMenu/DropdownMenu'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'

defineOptions({
  name: 'CheckinsSettingsLayout'
})

const { t } = useI18n()

const PAGES = [
  {
    value: ROUTE_NAMES.SETTINGS_AUTOMATIONS,
    label: t('checkins.automations'),
    trackingTab: 'checkins.automations'
  },
  {
    value: ROUTE_NAMES.SETTINGS_CHECK_INS_TEMPLATES,
    label: t('checkins.templates'),
    trackingTab: 'checkins.templates'
  }
]

const route = useRoute()
const currentView = computed(() => route.name)
const router = useRouter()

const store = useStore()

const userRoleForTracking = computed(() => {
  return store.getters['system/userRoleForTracking']
})

const onViewChange = view => {
  const page = PAGES.find(page => page.value === view)
  if (page) {
    router.push({ name: view })

    const trackerData = {
      category: EVENT_CATEGORIES.SETTINGS,
      subCategory: t('menu.checkins', 1, { locale: 'en' }),
      role: userRoleForTracking.value
    }

    if (page.trackingTab) {
      trackerData.tab = t(page.trackingTab, 1, { locale: 'en' })
    }

    tracker.logEvent(EVENT_NAMES.SETTINGS_OPENED, trackerData)
  }
}

const isPluginAdmin = computed(() => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.IS_PLUGIN_ADMIN])

const ACTIONS = {
  GUIDE: 'guide',
  TUTORIAL: 'tutorial'
}

const itemMenuActions = [
  {
    name: ACTIONS.GUIDE,
    icon: 'book',
    title: 'action.read_guide'
  },
  {
    name: ACTIONS.TUTORIAL,
    icon: 'play-solid',
    title: 'action.watch_tutorial'
  }
]

const onActionClick = action => {
  if (action === ACTIONS.GUIDE) {
    openLink({
      url: CHECKINS_GUIDE_LINK,
      useBrowserWorkaround: true
    })
  } else if (action === ACTIONS.TUTORIAL) {
    openLink({
      url: CHECKINS_TUTORIAL_LINK,
      useBrowserWorkaround: true
    })
  }
}
</script>

<template>
  <div class="cs-Page">
    <PageContentHeader
      :level="3"
      :max-width="HEADER_SIZES.LG"
      :title="$t('schedules.title')"
      no-margin
      style="--padding-bottom: 9px; --align-items: flex-start"
    >
      <DropdownMenu
        :items="itemMenuActions"
        :offset="[0, 0]"
        :type="DROPDOWN_MENU_TYPES.DEFAULT_NEXT"
        preferred-position="bottom-end"
        @item-click="onActionClick($event)"
      >
        <template #activator>
          <AppButtonNext
            :size="BUTTON_SIZES_NEXT.SM"
            :variant="BUTTON_VARIANTS.LINK_GRAY"
            icon-before="menu-support"
          >
            {{ t('common.help') }}
          </AppButtonNext>
        </template>
      </DropdownMenu>
    </PageContentHeader>

    <div class="cs-Page_Toolbar">
      <AppRadioGroup
        :data-auto-testid="CHECKINS_SETTINGS_PAGE_TABS_TEST_ID"
        :data-testid="CHECKINS_SETTINGS_PAGE_TABS_TEST_ID"
        :model-value="currentView"
        :options="PAGES"
        :type="RADIO_GROUP_TYPES.TAB_LIKE"
        name="page-navigation"
        @update:model-value="onViewChange"
      />
    </div>

    <router-view v-if="isPluginAdmin" />
  </div>
</template>

<style lang="scss" scoped>
.cs-Page {
  --ob-automations-page-width: calc(822px + #{$page-left-padding} + #{$page-right-padding});
  --ob-pch-lg-max-width: var(--ob-automations-page-width);
}

.cs-Page_Toolbar {
  padding: 5px $page-right-padding 0 $page-left-padding;
  width: 100%;
  max-width: var(--ob-automations-page-width);
}
</style>
